import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRefresh, useTranslate, Button } from 'react-admin';
import { Grid } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import SvgIcon from '@material-ui/core/SvgIcon';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import { TextInput } from '~/components';
import { ArrowForwardCircleIcon, AssignIcon, UnassignIcon } from '~/img';
import { ApprovalAction } from '~/api/approvals';
import {
  useApprovalCaseActions,
  useApproveApprovalMutation,
  useCancelApprovalMutation,
  useClaimApprovalMutation,
  useRejectApprovalMutation,
  useUnclaimApprovalMutation,
  useGetApprovalCaseFromQueue,
  useGetApprovalCaseFromHistory,
} from '~/hooks/approvals';
import { ApprovalCaseDetail, ApprovalCaseStatus } from '~/types/approvalCase';
import { useGetTransaction } from '~/hooks/headTransactions';

interface Props {
  record: ApprovalCaseDetail;
  resource?: string;
  onActionEnd?: () => Promise<void>;
}

type ActionPhase =
  | 'confirm'
  | 'processing'
  | { status: ApprovalCaseStatus; caseId: string; failureMessage?: string };

export const ApprovalActions: FC<Props> = ({ record, resource, onActionEnd }) => {
  const { id, status } = record;
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.approvals.show.${key}`);
  const classes = useStyles();
  const forceRefresh = useRefresh();
  const history = useHistory();

  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState('');
  const resetNote = () => {
    setNote('');
    setNoteError('');
  };

  const fetchFromQueueMutation = useGetApprovalCaseFromQueue();
  const fetchFromHistoryMutation = useGetApprovalCaseFromHistory();
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const {
    data: actionsData,
    isError,
    refetch: refetchApprovalCaseActions,
  } = useApprovalCaseActions(id, {
    enabled: status === 'IN_PROGRESS',
  });
  const allowedActions: ApprovalAction[] = actionsData?.data?.actions ?? [];
  const has = (action: ApprovalAction) => allowedActions.includes(action);

  const claimMutation = useClaimApprovalMutation();
  const unclaimMutation = useUnclaimApprovalMutation();
  const approveMutation = useApproveApprovalMutation();
  const rejectMutation = useRejectApprovalMutation();
  const cancelMutation = useCancelApprovalMutation();

  const [isLoading, setIsLoading] = useState(false);

  const isTransaction = record?.actionType === 'TRANSACTION';
  const { refetch: refetchTransaction } = useGetTransaction(record?.objectId, {
    enabled: isTransaction,
    staleTime: Infinity,
  });

  const requireNote = (): boolean => {
    if (!note.trim()) {
      setNoteError(translate('ra.validation.required'));
      return false;
    }
    return true;
  };

  const runAction = async (action: () => Promise<unknown>): Promise<ApprovalCaseDetail | null> => {
    setIsLoading(true);
    try {
      await action();
      await delay(1);
      const updatedApprovalCase = await fetchFromQueueMutation.mutateAsync(id);
      if (updatedApprovalCase.status !== 'IN_PROGRESS') {
        throw Error();
      }
      await onActionComplete();
      return null;
    } catch (e) {
      while (isMounted.current) {
        try {
          const result = await fetchFromHistoryMutation.mutateAsync(id);
          if (resource === 'approvals/queue') {
            if (isTransaction) {
              refetchTransaction().catch(() => {});
            }
            history.push(`/approvals/history/${result.id}/show`);
            return null;
          }
          return result;
        } catch (e) {
          await delay(3);
        }
      }
      return null;
    } finally {
      resetNote();
      setIsLoading(false);
    }
  };

  const onActionComplete = async () => {
    if (resource === 'approvals/queue' && isTransaction) {
      await refetchTransaction().catch(() => {});
    }
    forceRefresh();
    await onActionEnd?.();
    await refetchApprovalCaseActions().catch(() => {});
  };

  const onClaim = () => runAction(() => claimMutation.mutateAsync({ id }));
  const onUnclaim = () => runAction(() => unclaimMutation.mutateAsync({ id }));
  const onApprove = () => runAction(() => approveMutation.mutateAsync({ id, note }));
  const onReject = () => runAction(() => rejectMutation.mutateAsync({ id, note }));
  const onCancel = () => runAction(() => cancelMutation.mutateAsync({ id, note }));

  if (allowedActions.length === 0 || isError || status !== 'IN_PROGRESS') return null;

  const noteInput = (
    <Grid container spacing={2} style={{ minWidth: 400 }}>
      <Grid item xs={12}>
        <TextInput
          autoFocus
          label={t('note')}
          value={note}
          onChange={(e: any) => {
            setNote(e.target.value);
            setNoteError('');
          }}
          multiline
          rows={4}
          rowsMax={20}
          fullWidth
          required
          error={!!noteError}
          helperText={noteError}
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      <Divider className={classes.divider} />
      <Box className={classes.section}>
        <Box className={classes.header}>
          <Typography variant='h6'>{t('actions')}</Typography>
        </Box>
        <Divider className={classes.divider} />
        <Box className={classes.actionList}>
          {(has('APPROVE') || has('REJECT')) && (
            <Box className={classes.actionRow2col}>
              {has('APPROVE') && (
                <ConfirmedActionRow
                  icon={<ThumbUpIcon />}
                  label={t('approve')}
                  color='success'
                  heading={t('approveConfirmTitle')}
                  content={noteInput}
                  onConfirm={onApprove}
                  onValidate={requireNote}
                  onComplete={onActionComplete}
                  onClose={resetNote}
                  loading={isLoading}
                />
              )}
              {has('REJECT') && (
                <ConfirmedActionRow
                  icon={<ThumbDownIcon />}
                  label={t('reject')}
                  color='error'
                  heading={t('rejectConfirmTitle')}
                  content={noteInput}
                  onConfirm={onReject}
                  onValidate={requireNote}
                  onComplete={onActionComplete}
                  onClose={resetNote}
                  loading={isLoading}
                />
              )}
            </Box>
          )}
          {has('CLAIM') && (
            <ActionRow
              icon={<AssignIcon />}
              label={t('claim')}
              color='primary'
              onClick={onClaim}
              loading={isLoading}
            />
          )}
          {has('UNCLAIM') && (
            <ActionRow
              icon={<UnassignIcon />}
              label={t('unclaim')}
              color='primary'
              onClick={onUnclaim}
              loading={isLoading}
            />
          )}
          {has('CANCEL') && (
            <ConfirmedActionRow
              icon={<BlockIcon />}
              label={t('cancel')}
              color='warning'
              heading={t('cancelConfirmTitle')}
              content={noteInput}
              onConfirm={onCancel}
              onValidate={requireNote}
              onComplete={onActionComplete}
              onClose={resetNote}
              loading={isLoading}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

const delay = (seconds: number) => {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
};

type ActionColor = 'primary' | 'success' | 'error' | 'warning';

interface ActionRowProps {
  icon: ReactNode;
  label: string;
  color: ActionColor;
  onClick?: () => void;
  loading?: boolean;
}

const ActionRow: FC<ActionRowProps> = ({ icon, label, color, onClick, loading }) => {
  const classes = useStyles();
  return (
    <ButtonBase className={classes.actionRow} onClick={onClick} disabled={loading}>
      {loading ? (
        <CircularProgress size={20} className={`${classes.actionIcon} ${classes[color]}`} />
      ) : (
        <SvgIcon className={`${classes.actionIcon} ${classes[color]}`}>{icon}</SvgIcon>
      )}
      <Typography variant='button' className={classes[color]}>
        {label}
      </Typography>
    </ButtonBase>
  );
};

interface ConfirmedActionRowProps extends ActionRowProps {
  heading: string;
  content: ReactNode;
  onConfirm: () => Promise<ApprovalCaseDetail | null>;
  onValidate?: () => boolean;
  onComplete: () => void;
  onClose?: () => void;
  loading: boolean;
}

const ConfirmedActionRow: FC<ConfirmedActionRowProps> = ({
  icon,
  label,
  color,
  heading,
  content,
  onConfirm,
  onValidate,
  onComplete,
  onClose,
  loading,
}) => {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<ActionPhase>('confirm');
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.approvals.show.${key}`);

  const isProcessing = phase === 'processing';
  const isResult = typeof phase === 'object';
  const result = isResult
    ? (phase as { status: ApprovalCaseStatus; caseId: string; failureMessage?: string })
    : null;

  const handleOpen = () => {
    setPhase('confirm');
    setOpen(true);
  };

  const handleClose = () => {
    if (isResult) {
      onComplete();
    }
    onClose?.();
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (onValidate && !onValidate()) return;
    setPhase('processing');
    try {
      const confirmed = await onConfirm();
      if (confirmed) {
        setPhase({
          status: confirmed.status,
          caseId: confirmed.id,
          failureMessage: confirmed.executionResult?.message,
        });
      } else {
        handleClose();
      }
    } catch {
      setPhase('confirm');
    }
  };

  return (
    <>
      <ActionRow icon={icon} label={label} color={color} onClick={handleOpen} loading={loading} />
      <Dialog
        open={open}
        onClose={isProcessing ? undefined : handleClose}
        disableBackdropClick={isProcessing}
        disableEscapeKeyDown={isProcessing}
      >
        <DialogTitle>
          {isProcessing ? t('processing') : isResult ? t(`result.${result!.status}`) : heading}
        </DialogTitle>
        <DialogContent style={{ minWidth: 400 }}>
          {isProcessing ? (
            <Box display='flex' justifyContent='center' py={3}>
              <CircularProgress />
            </Box>
          ) : isResult ? (
            <>
              <DialogContentText>{t(`result.message.${result!.status}`)}</DialogContentText>
              {result!.failureMessage && (
                <DialogContentText>{result!.failureMessage}</DialogContentText>
              )}
            </>
          ) : (
            content
          )}
        </DialogContent>
        {!isProcessing && (
          <DialogActions>
            {isResult ? (
              <>
                <Button label='ra.action.close' onClick={handleClose} />
                <Button
                  label={t('viewCase')}
                  href={`/approvals/history/${result!.caseId}/show`}
                  variant='contained'
                  target='_blank'
                  onClick={(e: any) => e.stopPropagation()}
                >
                  <SvgIcon>
                    <ArrowForwardCircleIcon />
                  </SvgIcon>
                </Button>
              </>
            ) : (
              <>
                <Button label='ra.action.cancel' onClick={handleClose} />
                <Button
                  label={translate('utils.confirmation.confirm')}
                  onClick={handleConfirm}
                  variant='contained'
                  loading={loading}
                >
                  <CheckCircleIcon />
                </Button>
              </>
            )}
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  divider: {
    marginLeft: -theme.spacing(2),
    marginRight: -theme.spacing(2),
  },
  section: {
    paddingTop: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  actionList: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
  },
  actionRow2col: {
    display: 'flex',
    gap: theme.spacing(1),
    '& > *': {
      flex: 1,
    },
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: theme.spacing(1, 0),
    textAlign: 'left',
    gap: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    justifyContent: 'flex-start',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionIcon: {
    fontSize: 20,
    flexShrink: 0,
  },
  primary: { color: theme.palette.primary.main },
  success: { color: theme.palette.success.main },
  error: { color: theme.palette.error.main },
  warning: { color: theme.palette.warning.main },
}));
